import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import { IInvitation, IActivateInvitationPayload, ICreateInvitationPayload } from 'entities/Invitation/Invitation.models';
import { invitationTransport } from 'entities/Invitation/Invitation.transport';

interface IState {
  invitation: IInvitation | null;
  isInvitationLoading: boolean;
  invitationError: string | null;
}

const initialState: IState = {
  invitation: null,
  isInvitationLoading: false,
  invitationError: null,
};

export const invitationSlice = createSlice({
  name: 'invitation',
  initialState,
  reducers: {
    setInvitation(state, action: PayloadAction<IInvitation | null>) {
      state.invitation = action.payload;
    },
    setIsInvitationLoading(state, action: PayloadAction<boolean>) {
      state.isInvitationLoading = action.payload;
    },
    setInvitationError(state, action: PayloadAction<string | null>) {
      state.invitationError = action.payload;
    },
  },
});

export const { setInvitation, setIsInvitationLoading, setInvitationError } = invitationSlice.actions;

export const selectInvitation = (state: RootState) => state.invitation.invitation;
export const selectIsInvitationLoading = (state: RootState) => state.invitation.isInvitationLoading;
export const selectInvitationError = (state: RootState) => state.invitation.invitationError;

export const invitationReducer = invitationSlice.reducer;

export const getInvitation = (hash: string) => async (dispatch: AppDispatch) => {
  dispatch(setIsInvitationLoading(true));

  try {
    const response = await invitationTransport.getInvitation(hash);
    dispatch(setInvitation(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInvitationLoading(false));
  }
};

export const activateInvitation = (payload: IActivateInvitationPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInvitationLoading(true));

  try {
    const response = await invitationTransport.activateInvitation(payload);
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsInvitationLoading(false));
  }
};

export const inviteUser = (payload: ICreateInvitationPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInvitationLoading(true));

  try {
    await invitationTransport.inviteUser(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setInvitationError);
  } finally {
    dispatch(setIsInvitationLoading(false));
  }
};
